import React from "react";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import { Calendar, Clock, X } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.scss";

// react-datepicker v8 types its props as a union (single / range / multiple); this wrapper is single-date only and
// types its own props, so the inner component is used untyped.
const RDP = ReactDatePicker as unknown as React.ComponentType<Record<string, unknown>>;

/** The portal the calendars open into, so a dialog or a scrolling card never clips them. */
const PORTAL_ID = "ui-datepicker-portal";

export type DatePickerMode = "date" | "time" | "datetime";

export type DatePickerProps = {
  selected: Date | null | undefined;
  onChange: (date: Date | null) => void;
  /** date (default) · time (a list of times) · datetime (calendar + times) */
  mode?: DatePickerMode;
  /** date-fns format of the shown value; defaults by mode: "dd MMM yyyy", "h:mm aa", "dd MMM yyyy, h:mm aa" */
  dateFormat?: string;
  placeholderText?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  minTime?: Date;
  maxTime?: Date;
  /** Minutes between times (time / datetime), default 15 */
  timeIntervals?: number;
  /** The heading of the time list, default "Time" */
  timeCaption?: string;
  filterDate?: (date: Date) => boolean;
  filterTime?: (time: Date) => boolean;
  highlightDates?: Date[];
  dayClassName?: (date: Date) => string;
  /** Month / year dropdowns in the header (birthdays and other far-away dates) */
  showMonthDropdown?: boolean;
  showYearDropdown?: boolean;
  yearDropdownItemNumber?: number;
  scrollableYearDropdown?: boolean;
  dropdownMode?: "scroll" | "select";
  /** A ✕ in the field that empties it (default true: the old text / native date fields could always be emptied) */
  isClearable?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  name?: string;
  /** sm 32px, md 40px (default) */
  size?: "sm" | "md";
  /** Full width (default true) */
  block?: boolean;
  /** Classes on the field */
  className?: string;
  popperPlacement?: "bottom-start" | "bottom-end" | "top-start" | "top-end" | "bottom" | "top";
  onSelect?: (date: Date | null) => void;
  onCalendarClose?: () => void;
  "aria-label"?: string;
};

const DEFAULT_FORMAT: Record<DatePickerMode, string> = {
  date: "dd MMM yyyy",
  time: "h:mm aa",
  datetime: "dd MMM yyyy, h:mm aa",
};

type TriggerProps = {
  value?: string;
  onClick?: () => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  mode: DatePickerMode;
  size: "sm" | "md";
  block: boolean;
  invalid: boolean;
  className?: string;
  onClear?: () => void;
  ariaLabel?: string;
  // react-datepicker's own field handlers: keyboard (ArrowDown into the days, Escape to close) and focus
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
};

// The field: looks like a kit Input, opens the calendar. react-datepicker clones it and passes value, onClick and its
// keyboard / focus handlers, which must reach the button for keyboard use.
const Trigger = React.forwardRef<HTMLButtonElement, TriggerProps>(function Trigger(
  { value, onClick, onKeyDown, onFocus, onBlur, placeholder, disabled, id, name, mode, size, block, invalid, className, onClear, ariaLabel },
  ref
) {
  const Icon = mode === "time" ? Clock : Calendar;
  return (
    <span className={["ui-date", `ui-date--${size}`, block ? "ui-date--block" : "", className].filter(Boolean).join(" ")}>
      <button
        ref={ref}
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        className={["ui-date__field", invalid ? "is-invalid" : "", value ? "" : "is-empty"].filter(Boolean).join(" ")}
      >
        <Icon className="ui-date__icon" size={16} aria-hidden="true" />
        <span className="ui-date__value">{value || placeholder || " "}</span>
      </button>
      {onClear && value && !disabled ? (
        <button type="button" className="ui-date__clear" aria-label="Clear" onClick={onClear}>
          <X size={14} />
        </button>
      ) : null}
    </span>
  );
});

/**
 * The brand date / time / date-and-time picker (a calendar and a time list, never the browser's own). Date API, like
 * react-datepicker which it wraps: `selected` is a Date or null, `onChange` gets a Date or null.
 */
export function DatePicker({
  selected,
  onChange,
  mode = "date",
  dateFormat,
  placeholderText,
  timeIntervals = 15,
  timeCaption = "Time",
  isClearable = true,
  disabled = false,
  invalid = false,
  id,
  name,
  size = "md",
  block = true,
  className,
  popperPlacement = "bottom-start",
  "aria-label": ariaLabel,
  ...rest
}: DatePickerProps): React.JSX.Element {
  return (
    <RDP
      {...rest}
      selected={selected ?? null}
      onChange={(date: Date | null) => onChange(date)}
      dateFormat={dateFormat ?? DEFAULT_FORMAT[mode]}
      showTimeSelect={mode !== "date"}
      showTimeSelectOnly={mode === "time"}
      timeIntervals={timeIntervals}
      timeCaption={timeCaption}
      disabled={disabled}
      portalId={PORTAL_ID}
      // react-datepicker copies these onto the field it clones, so they must be given to it too, not only the field
      placeholderText={placeholderText}
      id={id}
      name={name}
      popperPlacement={popperPlacement}
      popperClassName="ui-datepicker-popper"
      calendarClassName={`ui-datepicker ui-datepicker--${mode}`}
      wrapperClassName={["ui-date-wrap", block ? "ui-date-wrap--block" : ""].filter(Boolean).join(" ")}
      showPopperArrow={false}
      customInput={
        <Trigger
          mode={mode}
          size={size}
          block={block}
          invalid={invalid}
          className={className}
          id={id}
          name={name}
          placeholder={placeholderText}
          ariaLabel={ariaLabel}
          onClear={isClearable ? () => onChange(null) : undefined}
        />
      }
    />
  );
}

/** The value event of the string pickers below: the same shape as a native input's change event. */
export type DateValueEvent = { target: { value: string; name?: string } };

type StringPickerProps = Omit<DatePickerProps, "selected" | "onChange" | "mode" | "minDate" | "maxDate"> & {
  /** The value, as the native input had it ("" when empty) */
  value: string | null | undefined;
  /** Gets { target: { value, name } } like a native input's onChange */
  onChange: (event: DateValueEvent) => void;
  /** Same string format as `value` */
  min?: string;
  max?: string;
  placeholder?: string;
};

const FORMATS = { date: "YYYY-MM-DD", time: "HH:mm", datetime: "YYYY-MM-DDTHH:mm" } as const;

function makeStringPicker(mode: DatePickerMode, displayName: string) {
  const fmt = FORMATS[mode];
  const toDate = (v?: string | null) => {
    if (!v) return null;
    const m = mode === "time" ? moment(v, fmt) : moment(v, [fmt, moment.ISO_8601]);
    return m.isValid() ? m.toDate() : null;
  };
  const Picker = ({ value, onChange, min, max, placeholder, placeholderText, name, ...rest }: StringPickerProps) => (
    <DatePicker
      {...rest}
      name={name}
      mode={mode}
      placeholderText={placeholderText ?? placeholder}
      selected={toDate(value)}
      minDate={mode === "time" ? undefined : toDate(min)}
      maxDate={mode === "time" ? undefined : toDate(max)}
      onChange={(date) => onChange({ target: { value: date ? moment(date).format(fmt) : "", name } })}
    />
  );
  Picker.displayName = displayName;
  return Picker;
}

/** Drop-in for <input type="date">: value / min / max are "YYYY-MM-DD", onChange gets { target: { value } }. */
export const DateInput = makeStringPicker("date", "DateInput");
/** Drop-in for <input type="time">: value is "HH:mm". */
export const TimeInput = makeStringPicker("time", "TimeInput");
/** Drop-in for <input type="datetime-local">: value is "YYYY-MM-DDTHH:mm". */
export const DateTimeInput = makeStringPicker("datetime", "DateTimeInput");

export default DatePicker;
